import React, { useMemo } from 'react';
import { Card, Col, Row, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileInvoice, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { useQueryStates, parseAsInteger, parseAsIsoDateTime, parseAsString } from 'nuqs';
import PageBreadcrumb from 'components/common/PageBreadcrumb';
import { useBreakpoints } from 'providers/BreakpointsProvider';
import { useEmpresas } from 'providers/EmpresaProvider';
import { useNavigate } from 'react-router-dom';
import { useNFes } from '../../hooks/useNFe';
import { formatDateBrazil } from '../../utils/dateUtils';
import { NFeFiltros, NFeStatus, NFeSummary, getTipoNotaLabel } from '../../types/nfe';
import './nfe.css';

// Componentes
import NFeStatusBadge from './components/NFeStatusBadge';
import NFeFiltrosComponent from './components/NFeFiltros';
import NFeTabelaAcoes from './components/NFeTabelaAcoes';
import BotaoCriarNFe from './components/BotaoCriarNFe';
import BotaoImportarXmlNFe from './components/BotaoImportarXmlNFe';
import BotaoImportarPorChaveNFe from './components/BotaoImportarPorChaveNFe';

const nfeBreadcrumbItems = [
  { label: 'Operacional', url: '#!' },
  { label: 'Notas Fiscais', active: true },
];

const STATUS_ROW_CLASS: Record<NFeStatus, string> = {
  [NFeStatus.Rascunho]:    'nfe-table-row--rascunho',
  [NFeStatus.Completa]:    'nfe-table-row--completa',
  [NFeStatus.Processando]: 'nfe-table-row--processando',
  [NFeStatus.Autorizada]:  'nfe-table-row--autorizada',
  [NFeStatus.Rejeitada]:   'nfe-table-row--rejeitada',
  [NFeStatus.Cancelada]:   'nfe-table-row--cancelada',
};

const MOBILE_CARD_CLASS: Record<NFeStatus, string> = {
  [NFeStatus.Rascunho]:    'nfe-mobile-card--rascunho',
  [NFeStatus.Completa]:    'nfe-mobile-card--completa',
  [NFeStatus.Processando]: 'nfe-mobile-card--processando',
  [NFeStatus.Autorizada]:  'nfe-mobile-card--autorizada',
  [NFeStatus.Rejeitada]:   'nfe-mobile-card--rejeitada',
  [NFeStatus.Cancelada]:   'nfe-mobile-card--cancelada',
};

const STAT_CHIPS: { label: string; status?: NFeStatus; color: string }[] = [
  { label: 'Total',      status: undefined,           color: 'var(--bs-body-color)' },
  { label: 'Rascunhos',  status: NFeStatus.Rascunho,  color: '#64748b' },
  { label: 'Autorizadas', status: NFeStatus.Autorizada, color: '#10b981' },
  { label: 'Rejeitadas', status: NFeStatus.Rejeitada,  color: '#ef4444' },
];

const formatarMoeda = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);

const SKELETON_ROW_WIDTHS = [
  ['5.5rem', '10rem', '7rem', '5rem'],
  ['6rem',   '8rem',  '5rem', '5.5rem'],
  ['5rem',   '12rem', '9rem', '4.5rem'],
  ['6rem',   '7rem',  '6rem', '5rem'],
  ['5.5rem', '11rem', '8rem', '4.5rem'],
  ['6rem',   '9rem',  '7rem', '5rem'],
  ['5rem',   '8rem',  '5rem', '5.5rem'],
  ['5.5rem', '10rem', '6rem', '4.5rem'],
];

const NFeListaSkeleton: React.FC<{ multiEmpresa: boolean; isMobile: boolean }> = ({
  multiEmpresa,
  isMobile,
}) => {
  if (isMobile) {
    return (
      <div className="p-3 placeholder-glow">
        {[5.5, 7, 6, 8, 5].map((destW, i) => (
          <Card key={i} className="mb-2 border border-opacity-25 shadow-sm">
            <Card.Body className="p-3">
              <div className="d-flex justify-content-between align-items-start">
                <div style={{ flex: 1, marginRight: '0.75rem' }}>
                  <div className="d-flex gap-2 mb-2">
                    <span className="placeholder rounded" style={{ height: '1rem', width: '5rem' }} />
                    <span className="placeholder rounded" style={{ height: '1rem', width: '4.5rem', borderRadius: '1rem' }} />
                  </div>
                  <span className="placeholder d-block mb-1 rounded" style={{ height: '0.72rem', width: `${destW}rem` }} />
                  <span className="placeholder d-block rounded" style={{ height: '0.65rem', width: '4.5rem' }} />
                </div>
                <div className="text-end">
                  <span className="placeholder d-block ms-auto mb-1 rounded" style={{ height: '1rem', width: '5rem' }} />
                  <span className="placeholder d-block ms-auto rounded" style={{ height: '0.65rem', width: '3rem' }} />
                </div>
              </div>
            </Card.Body>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="table-responsive">
      <table className="table mb-0 nfe-table">
        <thead>
          <tr>
            <th className="text-body-secondary" style={{ width: '1%' }} />
            <th className="text-body-secondary">Nº / Série</th>
            <th className="text-body-secondary">Data Emissão</th>
            <th className="text-body-secondary">Tipo</th>
            {multiEmpresa && <th className="text-body-secondary">Empresa</th>}
            <th className="text-body-secondary">Destinatário</th>
            <th className="text-body-secondary text-end">Valor Total</th>
            <th className="text-body-secondary">Status</th>
            <th />
          </tr>
        </thead>
        <tbody className="placeholder-glow">
          {SKELETON_ROW_WIDTHS.map(([numW, destW, destCnpjW, valorW], i) => (
            <tr key={i} className="nfe-table-row">
              <td style={{ width: 0, padding: 0 }} />
              <td>
                <span className="placeholder d-block mb-1 rounded" style={{ height: '0.85rem', width: numW }} />
                <span className="placeholder d-block rounded" style={{ height: '0.62rem', width: '3rem' }} />
              </td>
              <td><span className="placeholder rounded" style={{ height: '0.8rem', width: '5rem' }} /></td>
              <td><span className="placeholder rounded" style={{ height: '0.8rem', width: '4rem' }} /></td>
              {multiEmpresa && <td><span className="placeholder rounded" style={{ height: '0.8rem', width: '7rem' }} /></td>}
              <td>
                <span className="placeholder d-block mb-1 rounded" style={{ height: '0.85rem', width: destW }} />
                <span className="placeholder d-block rounded" style={{ height: '0.62rem', width: destCnpjW }} />
              </td>
              <td className="text-end">
                <span className="placeholder d-block ms-auto mb-1 rounded" style={{ height: '0.9rem', width: valorW }} />
                <span className="placeholder d-block ms-auto rounded" style={{ height: '0.62rem', width: '3rem' }} />
              </td>
              <td><span className="placeholder rounded" style={{ height: '1.3rem', width: '5rem', borderRadius: '1rem' }} /></td>
              <td />
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const NFePage: React.FC = () => {
  const navigate = useNavigate();
  const { breakpoints } = useBreakpoints();
  const { selectedIds } = useEmpresas();
  const multiEmpresa = selectedIds.length > 1;

  const [queryParams, setQueryParams] = useQueryStates(
    {
      status: parseAsString,
      dataInicio: parseAsIsoDateTime,
      dataFim: parseAsIsoDateTime,
      pageNumber: parseAsInteger.withDefault(1),
      pageSize: parseAsInteger.withDefault(20),
    },
    { history: 'push' }
  );

  const filtros = useMemo<NFeFiltros>(
    () => ({
      empresaIds: selectedIds.length > 0 ? selectedIds : undefined,
      status: queryParams.status !== null ? (queryParams.status as NFeStatus) : undefined,
      dataInicio: queryParams.dataInicio?.toISOString(),
      dataFim: queryParams.dataFim?.toISOString(),
      pageNumber: queryParams.pageNumber,
      pageSize: queryParams.pageSize,
    }),
    [selectedIds, queryParams]
  );

  // Stats queries — fetch counts per status
  const baseEmpresaIds = selectedIds.length > 0 ? selectedIds : undefined;
  const allQuery = useNFes({ empresaIds: baseEmpresaIds, pageNumber: 1, pageSize: 1 });
  const rascunhoQuery = useNFes({ empresaIds: baseEmpresaIds, status: NFeStatus.Rascunho, pageNumber: 1, pageSize: 1 });
  const autorizadaQuery = useNFes({ empresaIds: baseEmpresaIds, status: NFeStatus.Autorizada, pageNumber: 1, pageSize: 1 });
  const rejeitadaQuery = useNFes({ empresaIds: baseEmpresaIds, status: NFeStatus.Rejeitada, pageNumber: 1, pageSize: 1 });

  const statCounts = [
    allQuery.data?.totalRegistros,
    rascunhoQuery.data?.totalRegistros,
    autorizadaQuery.data?.totalRegistros,
    rejeitadaQuery.data?.totalRegistros,
  ];

  const nfesQuery = useNFes(filtros);
  const isMobile = breakpoints.down('md');

  const handleNFeClick = (nfe: NFeSummary) => navigate(`/operacional/nfe/${nfe.id}`);
  const handlePageChange = (p: number) => setQueryParams({ pageNumber: p });

  const renderNFeCard = (nfe: NFeSummary) => (
    <Card
      key={nfe.id}
      className={`mb-2 border border-opacity-25 shadow-sm nfe-mobile-card ${MOBILE_CARD_CLASS[nfe.status] ?? ''}`}
      onClick={() => handleNFeClick(nfe)}
    >
      <Card.Body className="p-3">
        <div className="d-flex justify-content-between align-items-start mb-1">
          <div className="flex-grow-1 me-2">
            <div className="d-flex align-items-center gap-2 mb-1">
              <span className="nfe-doc-num">#{String(nfe.numero).padStart(6, '0')}</span>
              <NFeStatusBadge
                status={nfe.status}
                rejeicaoCstat={nfe.ultimaRejeicaoCstat}
                rejeicaoXMotivo={nfe.ultimaRejeicaoXMotivo}
              />
            </div>
            <div className="text-body-secondary small">{nfe.empresaNome}</div>
            {nfe.destinatarioNome && (
              <div className="text-body-secondary small">{nfe.destinatarioNome}</div>
            )}
            <div className="text-body-tertiary" style={{ fontSize: '0.72rem' }}>
              {formatDateBrazil(nfe.dataEmissao)} &nbsp;·&nbsp; {getTipoNotaLabel(nfe.tipoNota)}
            </div>
          </div>
          <div className="text-end">
            <div className="nfe-mono fw-semibold" style={{ fontSize: '0.95rem' }}>
              {formatarMoeda(nfe.valorTotal)}
            </div>
            <div className="text-body-tertiary" style={{ fontSize: '0.7rem' }}>
              {nfe.quantidadeItens} {nfe.quantidadeItens === 1 ? 'item' : 'itens'}
            </div>
          </div>
        </div>
        <div className="d-flex justify-content-end align-items-center mt-1">
          <FontAwesomeIcon icon={faChevronRight} className="text-body-tertiary" style={{ fontSize: '0.7rem' }} />
        </div>
      </Card.Body>
    </Card>
  );

  const renderNFeTable = () => (
    <div className="table-responsive">
      <table className="table mb-0 nfe-table">
        <thead>
          <tr>
            <th className="text-body-secondary" style={{ width: '1%' }}></th>
            <th className="text-body-secondary">Nº / Série</th>
            <th className="text-body-secondary">Data Emissão</th>
            <th className="text-body-secondary">Tipo</th>
            {multiEmpresa && <th className="text-body-secondary">Empresa</th>}
            <th className="text-body-secondary">Destinatário</th>
            <th className="text-body-secondary text-end">Valor Total</th>
            <th className="text-body-secondary">Status</th>
            <th className="text-body-secondary"></th>
          </tr>
        </thead>
        <tbody>
          {nfesQuery.data?.emissoes.map((nfe) => (
            <tr
              key={nfe.id}
              className={`nfe-table-row ${STATUS_ROW_CLASS[nfe.status] ?? ''}`}
              onClick={() => handleNFeClick(nfe)}
            >
              <td style={{ width: 0, padding: 0 }} />
              <td>
                <div className="nfe-doc-num">#{String(nfe.numero).padStart(6, '0')}</div>
                <div className="text-body-tertiary" style={{ fontSize: '0.7rem' }}>
                  Série {nfe.serie}
                </div>
              </td>
              <td className="text-body-secondary" style={{ fontSize: '0.83rem' }}>
                {formatDateBrazil(nfe.dataEmissao)}
              </td>
              <td>
                <span className="text-body-secondary" style={{ fontSize: '0.78rem' }}>
                  {getTipoNotaLabel(nfe.tipoNota)}
                </span>
              </td>
              {multiEmpresa && (
                <td>
                  <span className="text-body-secondary" style={{ fontSize: '0.78rem' }}>
                    {nfe.empresaNome}
                  </span>
                </td>
              )}
              <td>
                {nfe.destinatarioNome ? (
                  <div>
                    <div className="text-body-emphasis" style={{ fontSize: '0.83rem' }}>
                      {nfe.destinatarioNome}
                    </div>
                    {nfe.destinatarioCnpjCpf && (
                      <div className="nfe-mono text-body-tertiary" style={{ fontSize: '0.7rem' }}>
                        {nfe.destinatarioCnpjCpf}
                      </div>
                    )}
                  </div>
                ) : (
                  <span className="text-body-tertiary fst-italic" style={{ fontSize: '0.78rem' }}>
                    Sem destinatário
                  </span>
                )}
              </td>
              <td className="text-end">
                <span className="nfe-mono fw-semibold" style={{ fontSize: '0.875rem' }}>
                  {formatarMoeda(nfe.valorTotal)}
                </span>
                <div className="text-body-tertiary" style={{ fontSize: '0.68rem' }}>
                  {nfe.quantidadeItens} {nfe.quantidadeItens === 1 ? 'item' : 'itens'}
                </div>
              </td>
              <td>
                <NFeStatusBadge
                  status={nfe.status}
                  rejeicaoCstat={nfe.ultimaRejeicaoCstat}
                  rejeicaoXMotivo={nfe.ultimaRejeicaoXMotivo}
                />
              </td>
              <td onClick={(e) => e.stopPropagation()}>
                <NFeTabelaAcoes nfe={nfe} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderPaginacao = () => {
    if (!nfesQuery.data || nfesQuery.data.totalPaginas <= 1) return null;
    const { paginaAtual, totalPaginas } = nfesQuery.data;
    return (
      <div className="d-flex justify-content-between align-items-center px-3 py-2 border-top border-opacity-25">
        <span className="text-body-tertiary" style={{ fontSize: '0.75rem' }}>
          Página {paginaAtual} de {totalPaginas} &nbsp;·&nbsp; {nfesQuery.data.totalRegistros} registros
        </span>
        <div className="d-flex gap-1">
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => handlePageChange(paginaAtual - 1)}
            disabled={paginaAtual === 1}
          >
            ‹
          </button>
          {Array.from({ length: totalPaginas }, (_, i) => i + 1)
            .filter((p) => Math.abs(p - paginaAtual) <= 2 || p === 1 || p === totalPaginas)
            .map((p, idx, arr) => (
              <React.Fragment key={p}>
                {idx > 0 && arr[idx - 1] !== p - 1 && (
                  <button className="btn btn-sm btn-outline-secondary" disabled>…</button>
                )}
                <button
                  className={`btn btn-sm ${p === paginaAtual ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => handlePageChange(p)}
                >
                  {p}
                </button>
              </React.Fragment>
            ))}
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => handlePageChange(paginaAtual + 1)}
            disabled={paginaAtual === totalPaginas}
          >
            ›
          </button>
        </div>
      </div>
    );
  };

  return (
    <>
      <PageBreadcrumb items={nfeBreadcrumbItems} />

      <Row className="g-3">
        {/* Page header */}
        <Col xs={12}>
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
            <div>
              <h2 className="mb-1 text-body-emphasis" style={{ fontWeight: 700, letterSpacing: '-0.02em' }}>
                Notas Fiscais
              </h2>
              <p className="text-body-secondary mb-0 small">
                Emissão e controle de NF-e de venda, compra e devolução
              </p>
            </div>
            <div className="d-flex gap-2">
              <BotaoImportarXmlNFe variant="link" className="text-body px-0" />
              <BotaoImportarPorChaveNFe variant="link" className="text-body px-0" />
              <BotaoCriarNFe />
            </div>
          </div>
        </Col>

        {/* Stats chips */}
        <Col xs={12}>
          <div className={`nfe-stats${allQuery.isLoading ? ' placeholder-glow' : ''}`}>
            {STAT_CHIPS.map((chip, i) => {
              const count = statCounts[i];
              const isActive = (chip.status ?? null) === (queryParams.status ?? null);
              return (
                <button
                  key={chip.label}
                  type="button"
                  className={`nfe-stat-chip ${isActive ? 'active' : ''}`}
                  onClick={() => setQueryParams({ status: chip.status ?? null, pageNumber: 1 })}
                >
                  {count !== undefined ? (
                    <span
                      className="nfe-stat-chip__count"
                      style={{ color: isActive ? chip.color : undefined }}
                    >
                      {count}
                    </span>
                  ) : (
                    <span className="placeholder rounded" style={{ width: '2.5rem', height: '1.3rem' }} />
                  )}
                  <span className="nfe-stat-chip__label">{chip.label}</span>
                </button>
              );
            })}
          </div>
        </Col>

        {/* Main card */}
        <Col xs={12}>
          <Card className="border border-opacity-25 shadow-sm overflow-hidden">
            {/* Filter bar */}
            <Card.Body className="pb-0 pt-3 px-3">
              <NFeFiltrosComponent
                status={queryParams.status !== null ? (queryParams.status as NFeStatus) : undefined}
                dataInicio={queryParams.dataInicio?.toISOString()}
                dataFim={queryParams.dataFim?.toISOString()}
                onStatusChange={(status) => setQueryParams({ status: status ?? null, pageNumber: 1 })}
                onDataInicioChange={(dataInicio) =>
                  setQueryParams({
                    dataInicio: dataInicio ? new Date(dataInicio) : null,
                    pageNumber: 1,
                  })
                }
                onDataFimChange={(dataFim) =>
                  setQueryParams({
                    dataFim: dataFim ? new Date(dataFim) : null,
                    pageNumber: 1,
                  })
                }
              />
            </Card.Body>

            {nfesQuery.isLoading && (
              <NFeListaSkeleton multiEmpresa={multiEmpresa} isMobile={isMobile} />
            )}

            {/* Error */}
            {nfesQuery.isError && (
              <div className="px-3 pb-3">
                <Alert variant="danger" className="border border-danger border-opacity-25 bg-danger-subtle mb-0">
                  <div className="text-danger-emphasis small">Erro ao carregar NF-es. Tente novamente.</div>
                </Alert>
              </div>
            )}

            {/* Empty */}
            {nfesQuery.isSuccess && nfesQuery.data.emissoes.length === 0 && (
              <div className="text-center py-5 px-3">
                <FontAwesomeIcon
                  icon={faFileInvoice}
                  className="text-body-tertiary mb-3"
                  style={{ fontSize: '2.5rem' }}
                />
                <h6 className="text-body-secondary">Nenhuma NF-e encontrada</h6>
                <p className="text-body-tertiary small mb-0">
                  {queryParams.status !== null || queryParams.dataInicio || queryParams.dataFim
                    ? 'Ajuste os filtros para ver mais resultados.'
                    : 'Clique em "Nova NF-e" para emitir sua primeira nota.'}
                </p>
              </div>
            )}

            {/* Data */}
            {nfesQuery.isSuccess && nfesQuery.data.emissoes.length > 0 && (
              <>
                {isMobile ? (
                  <div className="p-3">
                    {nfesQuery.data.emissoes.map(renderNFeCard)}
                  </div>
                ) : (
                  renderNFeTable()
                )}
                {renderPaginacao()}
              </>
            )}
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default NFePage;
